/**
 * Third-party checkouts the Storedum drawer can hand the shopper to on a
 * Shopify storefront, instead of Shopify's own checkout.
 *
 * Why this exists: a checkout app like GoKwik takes over checkout by finding
 * the theme's own "Checkout" buttons on the page and hooking them. The
 * drawer's button renders in our iframe, out of that script's reach, so the
 * drawer used to send every shopper straight to /checkout and the store's
 * checkout app never opened.
 *
 * Each provider is opened through the public function it puts on the page,
 * verified on a live store before it is listed here — GoKwik's
 * `triggerGokwikCustomCheckout()` on maharanafoods.com, 2026-09-30. The ids
 * are the cart designer's `checkout.provider` values and match the admin's
 * storefront scan (ecommerce-server functions/integrations/shopify/
 * storefront_checkouts.py).
 */

export type CheckoutProviderId = "gokwik";

/** The cart designer's choice: a provider, Shopify's own checkout, or "auto". */
export type CheckoutChoice = "auto" | "shopify" | CheckoutProviderId;

export interface ProviderWindow {
    triggerGokwikCustomCheckout?: () => void;
}

export interface CheckoutProvider {
    id: CheckoutProviderId;
    label: string;
    /** Its script is on the page, loaded or not. */
    present(doc: Document): boolean;
    /** Loaded — `open` works now. */
    ready(win: ProviderWindow): boolean;
    /** Open the provider's checkout for the page's Shopify cart. */
    open(win: ProviderWindow): void;
}

export const CHECKOUT_PROVIDERS: readonly CheckoutProvider[] = [
    {
        id: "gokwik",
        label: "GoKwik",
        present: (doc) => !!doc.querySelector('script[src*="gokwik.co"]'),
        ready: (win) => typeof win.triggerGokwikCustomCheckout === "function",
        open: (win) => {
            win.triggerGokwikCustomCheckout!();
        },
    },
];

/** How long a checkout waits for a provider whose script is on the page but
 *  has not finished loading, before it falls back to Shopify's checkout. */
export const PROVIDER_WAIT_MS = 3000;
const POLL_MS = 100;

/**
 * The provider this checkout should use, or null for Shopify's own checkout.
 *
 * "auto" (also the default for a missing or empty choice) picks the checkout
 * app that is on this page — what the theme's own cart does once the app is
 * installed. An explicit pick is honoured only when that app is on the page
 * too, and an unknown value means Shopify: a shopper is never sent to a
 * checkout that cannot open.
 */
export function chooseProvider(
    choice: string | null | undefined,
    win: ProviderWindow,
    doc: Document,
): CheckoutProvider | null {
    const value = String(choice ?? "").trim().toLowerCase() || "auto";
    if (value === "shopify") return null;
    const candidates = value === "auto" ? CHECKOUT_PROVIDERS : CHECKOUT_PROVIDERS.filter((p) => p.id === value);
    return candidates.find((p) => p.ready(win) || p.present(doc)) || null;
}

/** Whether `choice` names a specific checkout app (not "auto", not Shopify). */
export function isExplicitProvider(choice: string | null | undefined): boolean {
    const value = String(choice || "").trim().toLowerCase();
    return value !== "" && value !== "auto" && value !== "shopify";
}

/** Resolves true as soon as `provider` is ready, false after `waitMs`. */
export function whenReady(provider: CheckoutProvider, win: ProviderWindow, waitMs: number = PROVIDER_WAIT_MS): Promise<boolean> {
    if (provider.ready(win)) return Promise.resolve(true);
    return new Promise((resolve) => {
        const started = Date.now();
        const timer = setInterval(() => {
            if (provider.ready(win)) {
                clearInterval(timer);
                resolve(true);
            } else if (Date.now() - started >= waitMs) {
                clearInterval(timer);
                resolve(false);
            }
        }, POLL_MS);
    });
}
